import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui';

interface ReconcileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (accountingDate: string) => void;
  operationDescription?: string;
}

export const ReconcileModal = ({ isOpen, onClose, onConfirm, operationDescription }: ReconcileModalProps) => {
  const { t } = useTranslation();
  const [accountingDate, setAccountingDate] = useState('');

  // Reset date to today when modal opens
  useEffect(() => {
    if (isOpen) {
      setAccountingDate(new Date().toISOString().split('T')[0] || '');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handlePreviousDay = () => {
    const date = new Date(accountingDate);
    date.setDate(date.getDate() - 1);
    setAccountingDate(date.toISOString().split('T')[0] || '');
  };

  const handleNextDay = () => {
    const date = new Date(accountingDate);
    date.setDate(date.getDate() + 1);
    setAccountingDate(date.toISOString().split('T')[0] || '');
  };

  const handleConfirm = () => {
    onConfirm(accountingDate);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50" 
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-sm w-full mx-4 p-6">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
          {t('operations.reconcileModal.title', 'Rapprocher l\'opération')}
        </h3>
        
        {operationDescription && (
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 truncate">
            {operationDescription}
          </p>
        )}
        
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {t('operations.reconcileModal.accountingDate', 'Date comptable')}
          </label>
          
          <div className="flex items-center gap-2">
            {/* Previous day button */}
            <button
              type="button"
              onClick={handlePreviousDay}
              className="p-2 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              title={t('operations.reconcileModal.previousDay', 'Jour précédent')}
            >
              <svg className="w-5 h-5 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            {/* Date input */}
            <input
              type="date"
              value={accountingDate}
              onChange={(e) => setAccountingDate(e.target.value)}
              className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 text-center focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            />
            
            {/* Next day button */}
            <button
              type="button"
              onClick={handleNextDay}
              className="p-2 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              title={t('operations.reconcileModal.nextDay', 'Jour suivant')}
            >
              <svg className="w-5 h-5 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Buttons */}
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button onClick={handleConfirm}>
            {t('operations.reconcileModal.confirm', 'Rapprocher')}
          </Button>
        </div>
      </div>
    </div>
  );
};
